import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, downloadFile, get, patch, post, put, withQuery } from "./client";
import type { SiteRef, Supplier } from "./quotations";

/** `/api/v1/purchase-orders`, as `prv.web.api.purchase_orders` shapes it. */

export type OrderStatus =
  | "pending_generation"
  | "draft"
  | "generated"
  | "issued"
  | "sent"
  | "acknowledged"
  | "cancelled";

export interface SupplierInvoice {
  number: string | null;
  date: string | null;
  amount: string | null;
}

export interface OrderRow {
  id: number;
  number: string;
  status: OrderStatus;
  supplier: Supplier | null;
  site: SiteRef | null;
  inspection_id: number | null;
  quotation_id: number | null;
  reference: string | null;
  po_date: string | null;
  eta: string | null;
  line_count: number;
  total_excl_vat: string;
  unpriced: number;
  supplier_invoice: SupplierInvoice | null;
}

export interface OrderLine {
  id: number;
  item_code: number;
  supplier_code: string | null;
  details: string;
  units: string;
  unit_cost: string;
  discount_pct: string;
  amount: string;
  size: string | null;
  face_to_face: string | null;
  pressure_nominal: string | null;
  delivery: string | null;
  spare_part_id: number | null;
}

export interface OrderDetail {
  id: number;
  number: string;
  status: OrderStatus;
  reference: string | null;
  po_date: string | null;
  eta: string | null;
  vat_rate: string;
  expiry_ack: string | null;
  issued_at: string | null;
  acknowledged_at: string | null;
  /** When the issued order went to the supplier. A send dated in the past is
   * stored at midday, so show the day, not the time. */
  sent_at: string | null;
  sent_to: string | null;
  /** Who recorded the send: an email address; null for a supplier account. */
  sent_by: string | null;
  /** "marked" by the office, or "emailed" once the app sends orders itself. */
  sent_how: string | null;
  /** The address this supplier's last order went to, to fill the form; the
   * office only. */
  suggested_sent_to: string | null;
  supplier: Supplier | null;
  site: SiteRef | null;
  inspection_id: number | null;
  quotation: { id: number; number: string; status: string } | null;
  lines: OrderLine[];
  totals: { total_excl_vat: string; vat: string; grand_total: string; vatable: boolean };
  supplier_invoice: SupplierInvoice | null;
  actions: {
    office: boolean;
    transitions: OrderStatus[];
    can_price: boolean;
    can_issue: boolean;
    can_mark_sent: boolean;
    can_acknowledge: boolean;
    can_record_invoice: boolean;
    can_cancel: boolean;
    /** Why cancelling cannot also reopen the quotation for revision; null when it can. */
    revise_blocker: string | null;
  };
  pdf_url: string;
  /** The order and its quotation as one PDF: what the supplier is sent. */
  pack_url: string;
}

export const ORDERS_KEY = ["purchase-orders"] as const;
export const orderKey = (id: number) => ["purchase-orders", id] as const;

export function useOrders(search: string) {
  return useQuery<{ rows: OrderRow[] }, ApiError>({
    queryKey: [...ORDERS_KEY, { search }],
    queryFn: () => get<{ rows: OrderRow[] }>(withQuery("/api/v1/purchase-orders", { search })),
  });
}

export function useOrder(id: number | null) {
  return useQuery<OrderDetail, ApiError>({
    queryKey: orderKey(id ?? 0),
    queryFn: () => get<OrderDetail>(`/api/v1/purchase-orders/${id}`),
    enabled: id !== null,
  });
}

function useOrderWrite<TVars>(
  write: (vars: TVars) => Promise<OrderDetail>,
  after?: (detail: OrderDetail, vars: TVars) => void,
) {
  const client = useQueryClient();
  return useMutation<OrderDetail, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (detail, vars) => {
      client.setQueryData(orderKey(detail.id), detail);
      void client.invalidateQueries({ queryKey: ORDERS_KEY });
      // The quotation's own tabs show this order's status.
      void client.invalidateQueries({ queryKey: ["quotations"] });
      after?.(detail, vars);
    },
  });
}

export function useUpdateOrderHeader(id: number) {
  return useOrderWrite((body: { po_date: string | null; reference: string | null; eta: string | null }) =>
    patch<OrderDetail>(`/api/v1/purchase-orders/${id}`, body),
  );
}

export function usePriceLine(id: number) {
  return useOrderWrite((vars: { lineId: number; unit_cost: string; discount_pct: string }) =>
    put<OrderDetail>(`/api/v1/purchase-orders/${id}/lines/${vars.lineId}`, {
      unit_cost: vars.unit_cost,
      discount_pct: vars.discount_pct,
    }),
  );
}

export type OrderMove = "issue" | "acknowledge" | "cancel";

/** Issue, acknowledge or cancel outright. Issuing hands over the one PDF the
 * supplier is sent -- the order with the quotation it answers -- from either
 * Issue button: this screen's or the job's next-step banner. */
export function useOrderMove(id: number) {
  return useOrderWrite(
    (move: OrderMove) => post<OrderDetail>(`/api/v1/purchase-orders/${id}/${move}`),
    (detail, move) => {
      if (move === "issue") {
        downloadFile(detail.pack_url);
      }
    },
  );
}

/** The office recording that the issued order has been emailed, to whom and on
 * which day ("YYYY-MM-DD"). The server refuses a bad address, a future day, or
 * a day before the order was issued, with its reason as the error message. */
export function useMarkSent(id: number) {
  return useOrderWrite((body: { to: string; sent_on: string }) =>
    post<OrderDetail>(`/api/v1/purchase-orders/${id}/sent`, body),
  );
}

/** Cancel the order, with the reason, and -- with `revise` -- take its
 * quotation back so its lines can be changed and it can be resubmitted and
 * approved into a replacement order. */
export function useCancelOrder(id: number) {
  return useOrderWrite((body: { reason: string | null; revise: boolean }) =>
    post<OrderDetail>(`/api/v1/purchase-orders/${id}/cancel`, body),
  );
}

export function useRecordSupplierInvoice(id: number) {
  return useOrderWrite((body: { number: string; amount: string; invoice_date: string | null; replace: boolean }) =>
    post<OrderDetail>(`/api/v1/purchase-orders/${id}/supplier-invoice`, body),
  );
}

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  pending_generation: "Pending",
  draft: "Draft",
  generated: "Generated",
  issued: "Issued",
  sent: "Sent",
  acknowledged: "Acknowledged",
  cancelled: "Cancelled",
};
